import { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { setCurrentFocusField } from '../store/slice';

type NavItemProps = {
    id: string;
    children: ReactNode | ReactNode[];
};

// Height of top navbar with a 32px buffer
const TOP_BOUNDARY = 56 + 32;
// 56px from bottom of the screen
const BOTTOM_BOUNDARY = 56;

function NavItem({ id, children }: NavItemProps): JSX.Element {
    const dispatch = useDispatch();

    /**
     * Scrolls to an element with the matching id within the custom worksheet form
     * If the element is collapsed, its path will be expanded and then it will be scrolled to
     * A flashing animation will play once scrolling has stopped
     */
    function navigateToItem(): void {
        dispatch(setCurrentFocusField({ id, playAnimation: false }));

        let finishedScrollingInterval: string | number | NodeJS.Timer | undefined;
        let formElementTop = 0;
        let formElementBottom = 0;

        const finishedExpandingInterval = setInterval(waitForElementExpand, 200);

        function waitForElementExpand(): void {
            const element = document.getElementById(id);

            if (element) {
                const rect = element.getBoundingClientRect();

                // Check if the top and bottom of the element equals to the previous top and bottom
                const elementMovementStopped = rect.top === formElementTop && rect.bottom === formElementBottom;

                // When element stops moving (end of expand animation), start scrolling element into view
                if (elementMovementStopped) {
                    // Element is already in view if it's between the top and bottom boundary
                    const elementInView = rect.bottom <= window.innerHeight - BOTTOM_BOUNDARY && rect.top >= TOP_BOUNDARY;

                    formElementTop = 0;
                    formElementBottom = 0;
                    clearInterval(finishedExpandingInterval);

                    if (!elementInView) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                        finishedScrollingInterval = setInterval(waitForScroll, 300);
                    } else {
                        finishedScrollingInterval = setInterval(waitForScroll);
                    }
                    return;
                }

                formElementTop = rect.top;
                formElementBottom = rect.bottom;
            }
        }

        /**
         * Wait for the browser to scroll to the element
         * The browser doesn't provide us a callback to wait for the scroll to finish
         */
        function waitForScroll(): void {
            const element = document.getElementById(id);

            if (element) {
                const rect = element.getBoundingClientRect();
                const elementMovementStopped = rect.top === formElementTop && rect.bottom === formElementBottom;

                /**
                 * When element stops moving (end of scroll animation)
                 * Clear active field, set focus field
                 */
                if (elementMovementStopped) {
                    dispatch(setCurrentFocusField({ id, playAnimation: true }));
                    clearInterval(finishedScrollingInterval);
                    return;
                }

                formElementTop = rect.top;
                formElementBottom = rect.bottom;
            }
        }
    }

    return (
        <div
            className="cursor-pointer grow"
            onClick={navigateToItem}
            onMouseDown={(e): void => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            {children}
        </div>
    );
}

export default NavItem;
